import { CuideiPathology } from 'types/cuidei'
import { CuideiLandingContent } from 'types/cuideiLanding'

import { compact, JsonLdGraph, JsonLdNode } from '../../medleveSeo/jsonLd/graph'
import { CUIDEI_ORIGIN } from '../technicalSeo'

const OMENS_ORIGIN = 'https://www.omens.com.br'
const MEDLEVE_TEAM = 'https://www.medleve.com/equipe-medica'
const LANGUAGE = 'pt-BR'
const OG_IMAGE = '/landings/cuidei-hero-doctor.png'

export const landingUrl = (slug: string): string => `${CUIDEI_ORIGIN}/${slug}`

const organizationId = `${CUIDEI_ORIGIN}/#organization`
const websiteId = `${CUIDEI_ORIGIN}/#website`
const omensId = `${OMENS_ORIGIN}/#organization`

/** Official Cuidei profiles (PRD insumos, 2026-10-21). Reclame Aqui joins once the Cuidei profile exists. */
export const CUIDEI_SAME_AS = [
  'https://www.instagram.com/cuidei.br',
  'https://www.facebook.com/profile.php?id=61595724833117'
]

/**
 * The doctors are the Medleve team (same group): their Medleve profile is their author page, and the
 * Person `@id`/`url` are exactly the ones medleve.com publishes, so engines see one doctor cited by both sites.
 */
export const medleveProfileUrl = (profileSlug: string): string => `${MEDLEVE_TEAM}/${profileSlug}`
const personId = (profileSlug: string): string => `${medleveProfileUrl(profileSlug)}#person`

/** Author and medical reviewer of every pathology landing; also the Cuidei technical director. */
export const CUIDEI_MEDICAL_AUTHOR = {
  profileSlug: 'joao-brunhara',
  displayName: 'Dr. João Brunhara',
  crm: 'CRM-SP 161642'
}
const authorRef = { '@id': personId(CUIDEI_MEDICAL_AUTHOR.profileSlug) }

/** Name and published facts as on medleve.com/equipe-medica (read 2026-10-21). */
const featuredDoctors: JsonLdNode[] = [
  {
    '@type': 'Person',
    '@id': personId('joao-brunhara'),
    name: 'João Arthur Brunhara Alves Barbosa',
    alternateName: 'João Brunhara',
    honorificPrefix: 'Dr.',
    jobTitle: 'Médico, responsável técnico da Cuidei',
    url: medleveProfileUrl('joao-brunhara'),
    image: `${CUIDEI_ORIGIN}/landings/dr-joao-brunhara.webp`,
    identifier: { '@type': 'PropertyValue', propertyID: 'CRM-SP', value: '161642' },
    worksFor: { '@id': organizationId }
  },
  {
    '@type': 'Person',
    '@id': personId('carlos-seraphim'),
    name: 'Carlos Eduardo Seraphim',
    alternateName: 'Carlos Seraphim',
    honorificPrefix: 'Dr.',
    jobTitle: 'Médico endocrinologista',
    url: medleveProfileUrl('carlos-seraphim'),
    image: `${CUIDEI_ORIGIN}/landings/dr-carlos-seraphim.webp`,
    identifier: { '@type': 'PropertyValue', propertyID: 'CRM-SP', value: '156663' }
  }
]

/** Cuidei, Grupo Omens and the website: the same nodes on every Cuidei page. */
const cuideiBrandNodes = (): JsonLdNode[] => [
  {
    '@type': ['Organization', 'MedicalOrganization'],
    '@id': organizationId,
    name: 'Cuidei',
    url: CUIDEI_ORIGIN,
    logo: { '@type': 'ImageObject', url: `${CUIDEI_ORIGIN}/cuidei/cuidei-logo-512.png`, width: 512, height: 146 },
    description: 'Consultas médicas por vídeo, sem agendamento, com médicos brasileiros com CRM ativo.',
    areaServed: 'BR',
    sameAs: CUIDEI_SAME_AS,
    parentOrganization: { '@id': omensId }
  },
  { '@type': 'Organization', '@id': omensId, name: 'Grupo Omens', url: OMENS_ORIGIN },
  {
    '@type': 'WebSite',
    '@id': websiteId,
    url: CUIDEI_ORIGIN,
    name: 'Cuidei',
    inLanguage: LANGUAGE,
    publisher: { '@id': organizationId }
  }
]

/** "R$ 79" → "79.00" (schema.org prices are plain decimals). */
const toPrice = (label: string): string | undefined => {
  const match = label.replace(/\./g, '').match(/\d+(,\d+)?/)
  return match ? Number(match[0].replace(',', '.')).toFixed(2) : undefined
}

/** "CRM-SP 267456" → { uf: 'SP', number: '267456' }. */
const parseCrm = (crm: string) => {
  const match = crm.match(/CRM[-\s]?([A-Z]{2})\s*([\d.-]+)/)
  return match ? { uf: match[1], number: match[2] } : undefined
}

const absolute = (url?: string) => (url?.startsWith('/') ? `${CUIDEI_ORIGIN}${url}` : url)

/** The rendered editorial sections (brief 2026-10-26), in page order, with their visible titles. */
const editorialParts = (editorial: CuideiLandingContent['editorial']): { id: string; name: string }[] => {
  if (!editorial) return []
  const { prevalence, presentations, recognition, estimates, doctorMessage } = editorial
  return [
    prevalence && { id: prevalence.id, name: prevalence.title },
    presentations && { id: presentations.id, name: presentations.title },
    recognition && { id: recognition.id, name: recognition.title },
    estimates && { id: estimates.id, name: estimates.title },
    doctorMessage && { id: doctorMessage.id, name: `Mensagem de ${doctorMessage.name}` }
  ].filter((part): part is { id: string; name: string } => Boolean(part))
}

/**
 * Entity graph for a pathology landing, built from the SAME content that is rendered, so every
 * claim in the JSON-LD is visible on the page. Placeholder strings ("[substituir]") are never emitted.
 */
export const buildLandingJsonLd = (slug: string, content: CuideiLandingContent): JsonLdGraph => {
  const url = landingUrl(slug)
  const pageId = `${url}#webpage`
  const { condition } = content
  const conditionId = `${url}#condition`
  const serviceId = `${url}#service`
  const breadcrumbId = `${url}#breadcrumb`
  const isReal = (text?: string) => !!text && !text.includes('[substituir]')
  // "TDAH em adultos": how the page names the condition for patients (breadcrumb, service).
  const conditionLabel = content.hero.eyebrow ?? condition?.name
  const adults = { '@type': 'PeopleAudience', audienceType: 'Adultos' }

  const doctors = (content.doctors?.items ?? []).filter((doctor) => isReal(doctor.name) && isReal(doctor.crm))
  const faq = (content.faq?.items ?? []).filter((item) => isReal(item.question) && isReal(item.answer))
  const price = content.offer ? toPrice(content.offer.price) : undefined

  const nodes: JsonLdNode[] = [
    ...cuideiBrandNodes(),
    ...featuredDoctors,
    {
      '@type': 'BreadcrumbList',
      '@id': breadcrumbId,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: `${CUIDEI_ORIGIN}/` },
        compact({ '@type': 'ListItem', position: 2, name: conditionLabel ?? content.hero.headline, item: url })
      ]
    },
    compact({
      '@type': ['WebPage', 'MedicalWebPage'],
      '@id': pageId,
      url,
      name: content.seo?.title ?? content.hero.headline,
      description: content.seo?.description,
      inLanguage: LANGUAGE,
      isPartOf: { '@id': websiteId },
      breadcrumb: { '@id': breadcrumbId },
      about: condition ? { '@id': conditionId } : undefined,
      mainEntity: { '@id': serviceId },
      audience: adults,
      author: authorRef,
      reviewedBy: authorRef,
      datePublished: content.datePublished,
      dateModified: content.dateModified,
      lastReviewed: content.dateModified,
      citation: (content.references ?? []).map((reference) => reference.url),
      // One element per editorial section and per guide H2 (page order): engines quote anchored passages.
      hasPart: [
        ...editorialParts(content.editorial).map(({ id, name }) => ({
          '@type': 'WebPageElement',
          '@id': `${url}#${id}`,
          name,
          cssSelector: `#${id}`,
          isPartOf: { '@id': pageId }
        })),
        ...(content.guide?.sections ?? []).map((section) => ({
          '@type': 'WebPageElement',
          '@id': `${url}#${section.id}`,
          name: section.h2,
          cssSelector: `#${section.id}`
        }))
      ],
      publisher: { '@id': organizationId }
    })
  ]

  if (condition) {
    const named = (type: string) => (name: string) => ({ '@type': type, name })
    nodes.push(
      compact({
        '@type': 'MedicalCondition',
        '@id': conditionId,
        name: condition.name,
        alternateName: condition.alternateName,
        code: (
          [
            ['ICD-10', condition.icd10],
            ['ICD-11', condition.icd11]
          ] as const
        )
          .filter(([, codeValue]) => codeValue)
          .map(([codingSystem, codeValue]) => ({
            '@type': 'MedicalCode',
            codeValue: codeValue as string,
            codingSystem
          })),
        relevantSpecialty: condition.specialty ? `https://schema.org/${condition.specialty}` : undefined,
        signOrSymptom: (condition.symptoms ?? []).map(named('MedicalSignOrSymptom')),
        possibleTreatment: (condition.possibleTreatments ?? []).map(named('MedicalTherapy')),
        riskFactor: (condition.riskFactors ?? []).map(named('MedicalRiskFactor')),
        differentialDiagnosis: (condition.differentialDiagnoses ?? []).map((name) => ({
          '@type': 'DDxElement',
          diagnosis: { '@type': 'MedicalCondition', name }
        })),
        epidemiology: condition.epidemiology?.replace(/\s*\[\d+\]/g, ''),
        typicalTest: condition.typicalTest ? { '@type': 'MedicalTest', name: condition.typicalTest } : undefined,
        sameAs: condition.sameAs
      })
    )
  }

  nodes.push(
    compact({
      '@type': 'Service',
      '@id': serviceId,
      name: `Consulta médica online para ${conditionLabel ?? 'sua saúde'}`,
      serviceType: 'Consulta médica por telemedicina',
      about: condition ? { '@id': conditionId } : undefined,
      audience: adults,
      provider: { '@id': organizationId },
      areaServed: 'BR',
      availableChannel: { '@type': 'ServiceChannel', serviceUrl: `${url}/consulta`, name: 'Consulta por vídeo' },
      offers: price
        ? { '@type': 'Offer', price, priceCurrency: 'BRL', url, availability: 'https://schema.org/InStock' }
        : undefined
    })
  )

  doctors.forEach((doctor, index) => {
    const crm = parseCrm(doctor.crm)
    const profileUrl = doctor.profileSlug ? medleveProfileUrl(doctor.profileSlug) : undefined
    nodes.push(
      compact({
        '@type': 'Person',
        '@id': doctor.profileSlug ? personId(doctor.profileSlug) : `${url}#doctor-${index + 1}`,
        name: doctor.name.replace(/^Dra?\.\s*/, ''),
        honorificPrefix: doctor.name.match(/^Dra?\./)?.[0],
        jobTitle: 'Médico',
        url: profileUrl,
        image: absolute(doctor.photoUrl),
        worksFor: { '@id': organizationId },
        identifier: crm ? { '@type': 'PropertyValue', propertyID: `CRM-${crm.uf}`, value: crm.number } : undefined
      })
    )
  })

  if (faq.length) {
    nodes.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      inLanguage: LANGUAGE,
      isPartOf: { '@id': pageId },
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    })
  }

  return { '@context': 'https://schema.org', '@graph': nodes }
}

export const landingOgImage = `${CUIDEI_ORIGIN}${OG_IMAGE}`

export interface PathologySeo {
  title: string
  description?: string
  image: string
}

/**
 * Title / description / share image of a pathology page, as set in the admin
 * (landing.seo). Defaults only cover a record saved before seo was required.
 */
export const resolvePathologySeo = ({ landing }: Pick<CuideiPathology, 'landing'>): PathologySeo => {
  const string = (value: unknown): string | undefined =>
    typeof value === 'string' ? value.trim() || undefined : undefined
  return {
    title: string(landing?.seo?.title) || `${string(landing?.hero?.headline) ?? 'Consulta médica por vídeo'} | Cuidei`,
    description: string(landing?.seo?.description),
    image: string(landing?.seo?.imageUrl) || landingOgImage
  }
}

export const HUB_TITLE = 'Consulta médica online por vídeo: condições que tratamos'

/** cuidei.com.br/: CollectionPage + ItemList with one entry per active landing (PRD "Template GEO"). */
export const buildHubJsonLd = (
  cards: { slug: string; title: string; description?: string }[],
  description: string
): JsonLdGraph => {
  const url = `${CUIDEI_ORIGIN}/`
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...cuideiBrandNodes(),
      {
        '@type': 'CollectionPage',
        '@id': `${url}#webpage`,
        url,
        name: `${HUB_TITLE} | Cuidei`,
        description,
        inLanguage: LANGUAGE,
        isPartOf: { '@id': websiteId },
        about: { '@id': organizationId },
        publisher: { '@id': organizationId },
        mainEntity: { '@id': `${url}#condicoes` }
      },
      {
        '@type': 'ItemList',
        '@id': `${url}#condicoes`,
        name: 'Condições que tratamos',
        numberOfItems: cards.length,
        itemListElement: cards.map((card, index) =>
          compact({
            '@type': 'ListItem',
            position: index + 1,
            name: card.title,
            description: card.description || undefined,
            url: landingUrl(card.slug)
          })
        )
      }
    ]
  }
}
